import moment from "moment";
import { saveFile } from "../../../native/files";
import { isNative } from "../../../native/platform";

const activeEvents = events =>
  events
    .filter(e => !e.cancelled)
    .sort((a, b) => a.start - b.start || a.title.localeCompare(b.title));

const staffNames = event =>
  (event.employees || [])
    .map(emp => `${emp.firstName || ""} ${emp.lastName || ""}`.trim())
    .filter(Boolean);

const staffing = event =>
  event.minStaff > 0
    ? `${(event.employees || []).length}/${event.minStaff}`
    : `${(event.employees || []).length}`;

// ─── iCalendar ─────────────────────────────────────────────────────────────

const icsText = value =>
  String(value || "")
    .replace(/[\\;,]/g, m => `\\${m}`)
    .replace(/\n/g, "\\n");
const icsDate = date => moment(date).utc().format("YYYYMMDD[T]HHmmss[Z]");

export const exportICS = (events, fileName = "shifts.ics") => {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Shift Planning//EN",
    "CALSCALE:GREGORIAN",
    ...activeEvents(events).flatMap(event => [
      "BEGIN:VEVENT",
      `UID:${event._id}@shift-planning`,
      `DTSTAMP:${icsDate(new Date())}`,
      `DTSTART:${icsDate(event.start)}`,
      `DTEND:${icsDate(event.end)}`,
      `SUMMARY:${icsText(event.title)}`,
      `DESCRIPTION:${icsText(staffNames(event).join(", "))}`,
      "END:VEVENT"
    ]),
    "END:VCALENDAR"
  ];
  return saveFile(new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" }), fileName);
};

// ─── CSV (semicolon-separated so German Excel opens it directly) ───────────

const csvCell = value => `"${String(value ?? "").replace(/"/g, '""')}"`;

export const exportCSV = (events, fileName = "shifts.csv") => {
  const header = ["Date", "Weekday", "Start", "End", "Hours", "Shift", "Staff", "Employees"];
  const rows = activeEvents(events).map(event => [
    moment(event.start).format("DD.MM.YYYY"),
    moment(event.start).format("dddd"),
    moment(event.start).format("HH:mm"),
    moment(event.end).format("HH:mm"),
    ((event.end - event.start) / 36e5).toFixed(2).replace(".", ","),
    event.title,
    staffing(event),
    staffNames(event).join(", ")
  ]);
  const csv = [header, ...rows].map(row => row.map(csvCell).join(";")).join("\r\n");
  return saveFile(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }), fileName);
};

// ─── Print / PDF ───────────────────────────────────────────────────────────

const escapeHtml = value =>
  String(value ?? "").replace(
    /[&<>"']/g,
    c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );

// Opens a printable rota grouped by day; the browser's print dialog can save it as PDF
export const printShifts = (events, title) => {
  const byDay = {};
  activeEvents(events).forEach(event => {
    const day = moment(event.start).format("dddd, DD.MM.YYYY");
    (byDay[day] = byDay[day] || []).push(event);
  });
  const sections = Object.entries(byDay)
    .map(
      ([day, list]) => `
      <h2>${escapeHtml(day)}</h2>
      <table>
        <thead><tr><th>Time</th><th>Shift</th><th>Staff</th><th>Employees</th></tr></thead>
        <tbody>${list
          .map(
            e => `<tr class="${e.minStaff > (e.employees || []).length ? "under" : ""}">
              <td>${moment(e.start).format("HH:mm")}–${moment(e.end).format("HH:mm")}</td>
              <td><span class="dot" style="background:${escapeHtml(e.colorCode)}"></span>${escapeHtml(e.title)}</td>
              <td>${escapeHtml(staffing(e))}</td>
              <td>${escapeHtml(staffNames(e).join(", "))}</td></tr>`
          )
          .join("")}</tbody>
      </table>`
    )
    .join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
    <style>
      body { font: 12px/1.4 -apple-system, Segoe UI, Roboto, sans-serif; color: #111; margin: 24px; }
      h1 { font-size: 18px; margin: 0 0 16px; }
      h2 { font-size: 13px; margin: 18px 0 6px; break-after: avoid; }
      table { width: 100%; border-collapse: collapse; break-inside: avoid; }
      th, td { text-align: left; padding: 4px 8px; border-bottom: 1px solid #ddd; vertical-align: top; }
      th { background: #f4f4f4; }
      tr.under td:nth-child(3) { color: #c62828; font-weight: 600; }
      .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; }
      @page { margin: 14mm; }
    </style></head><body><h1>${escapeHtml(title)}</h1>${sections || "<p>No shifts in this period.</p>"}</body></html>`;
  // The app WebViews have no print windows; the share sheet offers Print for the page instead.
  if (isNative) {
    saveFile(new Blob([html], { type: "text/html" }), `${title}.html`);
    return true;
  }
  const win = window.open("", "_blank");
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
  return true;
};
